import { FarmRiskType } from '@ourbranch/lookups';

// #32526: farm mortgages are stored per-structure on `farmLocations[].risks[].building.mortgageDetails`
// (the representation the dec/docs read), not on the legacy flat `farm.mortgageDetails`. The staff-fe
// mortgage form keeps a single flat "Mortgage lender information" list where each mortgage carries a
// form-only `appliesToStructure` (a `locationId::riskId` ref chosen in the "Applies to structure"
// dropdown). These helpers convert between that flat form list and per-building storage.

// Structures that can carry a mortgage: owned dwellings (FO-1/2/3) and FO-6 structures
// (barns/outbuildings). FO-4 renters (tenant contents) and scheduled/unscheduled inland marine
// are not mortgageable.
export const MORTGAGEABLE_RISK_TYPES = [
  FarmRiskType.Fo1,
  FarmRiskType.Fo2,
  FarmRiskType.Fo3,
  FarmRiskType.Fo6Structures
];

export const isMortgageableRiskType = (type) => MORTGAGEABLE_RISK_TYPES.includes(type);

// Composite id used by the "Applies to structure" dropdown and to route a mortgage back to its
// building. Mirrors the `locationId::riskId` convention used elsewhere on the farm form.
export const structureRefId = (locationId, riskId) => `${locationId}::${riskId}`;

/**
 * Builds the "Applies to structure" dropdown options for the mortgage form: the subset of the
 * given risk options that correspond to mortgageable structures (owned dwellings + FO-6 barns).
 * Shared by the offer and policy-details render paths.
 * @param {Array} farmLocations - the form's farmLocations
 * @param {Array} riskOptions - `getRiskOptions(...)` output ({ id: 'loc::risk', value })
 */
export const getMortgageStructureOptions = (farmLocations, riskOptions) => {
  const mortgageableRiskIds = new Set();
  for (const location of farmLocations || []) {
    if (location?.isDeleted) {
      continue;
    }
    for (const risk of location.risks || []) {
      if (isMortgageableRiskType(risk.type)) {
        mortgageableRiskIds.add(structureRefId(location.id, risk.id));
      }
    }
  }
  return (riskOptions || []).filter((option) => mortgageableRiskIds.has(option.id));
};

/**
 * Flattens per-building mortgages into a single form list, tagging each with the form-only
 * `appliesToStructure` ref so the dropdown can show/edit the association. Skips deleted locations.
 */
export const flattenFarmMortgagesForForm = (farmLocations) => {
  const flat = [];
  for (const location of farmLocations || []) {
    if (location?.isDeleted) {
      continue;
    }
    for (const risk of location.risks || []) {
      for (const mortgage of risk.building?.mortgageDetails || []) {
        flat.push({ ...mortgage, appliesToStructure: structureRefId(location.id, risk.id) });
      }
    }
  }
  return flat;
};

/**
 * Distributes the flat form mortgage list back into each structure's `building.mortgageDetails`,
 * stripping the form-only `appliesToStructure`. Returns a NEW farmLocations array. Every building is
 * rewritten (buildings with no mortgage get an empty array) so removals and re-assignments persist.
 * A mortgage with no `appliesToStructure` is dropped — the agent must pick a structure.
 */
export const applyFarmMortgagesToLocations = (farmLocations, flatMortgages) => {
  const byStructure = new Map();
  for (const mortgage of flatMortgages || []) {
    const { appliesToStructure, ...rest } = mortgage || {};
    if (!appliesToStructure) {
      continue;
    }
    if (!byStructure.has(appliesToStructure)) {
      byStructure.set(appliesToStructure, []);
    }
    byStructure.get(appliesToStructure).push(rest);
  }

  return (farmLocations || []).map((location) => ({
    ...location,
    risks: (location.risks || []).map((risk) => {
      // Scheduled/unscheduled inland-marine risks have no building — leave them untouched.
      if (!risk.building) {
        return risk;
      }
      const ref = structureRefId(location.id, risk.id);
      return { ...risk, building: { ...risk.building, mortgageDetails: byStructure.get(ref) || [] } };
    })
  }));
};
